import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageCircle, Send, Loader2, ShieldAlert } from 'lucide-react';

/**
 * Reusable single-recipient WhatsApp send dialog.
 * Props:
 *  open, onOpenChange
 *  to, patientName, ticketId (all optional pre-fills)
 *  onSent (msg) => void
 */
export default function WhatsAppSendDialog({ open, onOpenChange, to = '', patientName = '', ticketId = null, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [recipient, setRecipient] = useState(to);
  const [params, setParams] = useState({});
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => { setRecipient(to); }, [to]);

  useEffect(() => {
    if (!open) return;
    api.get('/whatsapp/templates').then(r => {
      setTemplates(r.data.data || []);
      setEnabled(r.data.enabled !== false);
      if (!templateId && r.data.data?.length) setTemplateId(r.data.data[0].template_id);
    }).catch(() => toast.error('Failed to load templates'));
  }, [open]);

  const currentTpl = templates.find(t => t.template_id === templateId);

  useEffect(() => {
    // reset params + pre-fill {{0}} with patient_name whenever template changes
    const p = {};
    (currentTpl?.parameters || []).forEach((prm) => { p[prm.index] = ''; });
    if (p['0'] !== undefined && patientName) p['0'] = patientName;
    setParams(p);
  }, [templateId, currentTpl, patientName]);

  const submit = async () => {
    if (!recipient || !templateId) { toast.error('Recipient and template are required'); return; }
    setSending(true);
    try {
      const r = await api.post('/whatsapp/send', {
        to: recipient, template_id: templateId, parameters: params,
        ticket_id: ticketId, patient_name: patientName,
      });
      const st = r.data.status;
      if (st === 'sent') toast.success('WhatsApp sent');
      else if (st === 'skipped') toast.warning('Karix disabled — logged as skipped');
      else toast.error(`Send failed: ${r.data.error || 'unknown'}`);
      onSent && onSent(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wa-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            Send WhatsApp {patientName ? `to ${patientName}` : ''}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Pick a template and provide any placeholder values. The message is logged in the patient timeline.
          </DialogDescription>
        </DialogHeader>

        {!enabled && (
          <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2 flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Karix API key is <b>not configured</b>. Messages will be logged as <code>skipped</code>.
              Set <code>KARIX_API_KEY</code> in backend <code>.env</code> to enable live sends.
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Recipient (Mobile)</label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder="+91 98765 43210" className="h-9 mt-1" data-testid="wa-send-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Template</label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-9 mt-1" data-testid="wa-send-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} <span className="text-[10px] text-stone-400 ml-1">· {t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentTpl?.description && (
              <div className="text-[11px] text-stone-500 mt-1 leading-snug">{currentTpl.description}</div>
            )}
          </div>

          {(currentTpl?.parameters || []).length > 0 && (
            <div className="space-y-2 rounded border border-stone-200 p-2">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Parameters</div>
              {currentTpl.parameters.map((prm) => (
                <div key={prm.index}>
                  <div className="text-[11px] text-stone-600 mb-0.5">{`{{${Number(prm.index) + 1}}}`} · {prm.label}</div>
                  <Input value={params[prm.index] || ''}
                    onChange={(e) => setParams(p => ({ ...p, [prm.index]: e.target.value }))}
                    className="h-8 text-xs" data-testid={`wa-send-param-${prm.index}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || !recipient || !templateId}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-send-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
